import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AdminService from 'services/AdminService';
import { STATUS } from 'constants/miscConstants';
import { generateQueryParams } from 'utils/utils';
import { t } from 'i18next';

const { SUCCESS, ERROR } = STATUS;

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  status: SUCCESS,
  data: {},
  statsLoading: false,
  filters: {
    pageNumber: 1,
    pageSize: 10,
  },
  submitting: false,
  actionSuccess: false,
  companyOptions: [],
  companyOptionsLoading: false,
};

// Async thunk
export const getEmployerSubscription = createAsyncThunk('employerSubscription/getEmployerSubscription', async (data, { rejectWithValue }) => {
  const { queryParams, pageNumber, pageSize } = data;

  try {
    const response = await AdminService.getEmployerSubscription(queryParams);
    return { response, pageNumber, pageSize };
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const registerEmployer = createAsyncThunk('employerSubscription/registerEmployer', async (data, { rejectWithValue }) => {
  try {
    return await AdminService.registerEmployer(data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to register company');
  }
});

export const assignCustomPackage = createAsyncThunk('employerSubscription/assignCustomPackage', async (data, { rejectWithValue }) => {
  try {
    return await AdminService.assignCustomPackage(data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to assign custom package');
  }
});

export const assignPostpaidPackage = createAsyncThunk('employerSubscription/assignPostpaidPackage', async (data, { rejectWithValue }) => {
  try {
    return await AdminService.assignPostpaidPackage(data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to assign pay as you go package');
  }
});

export const cancelSubscription = createAsyncThunk('employerSubscription/cancelSubscription', async (data, { rejectWithValue }) => {
  try {
    return await AdminService.cancelSubscription(data);
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to cancel subscription');
  }
});

export const getCompanyList = createAsyncThunk('employerSubscription/getCompanyList', async (_, { rejectWithValue }) => {
  try {
    const response = await AdminService.getCompanies(generateQueryParams({ page: 0, size: 1000 }));
    return response?.companies || [];
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to load companies');
  }
});

const employerSubscriptionSlice = createSlice({
  name: 'employerSubscription',
  initialState,
  reducers: {
    hideMessage: (state) => {
      state.message = '';
      state.status = SUCCESS;
      state.showMessage = false;
      state.actionSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployerSubscription.pending, (state) => {
        state.loading = true;
      })
      .addCase(getEmployerSubscription.fulfilled, (state, action) => {
        const { response, pageNumber, pageSize } = action.payload;
        state.loading = false;
        state.data = response;
        state.filters = {
          pageNumber,
          pageSize,
        };
      })
      .addCase(getEmployerSubscription.rejected, (state, action) => {
        state.loading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(registerEmployer.pending, (state) => {
        state.submitting = true;
      })
      .addCase(registerEmployer.fulfilled, (state) => {
        state.submitting = false;
        state.status = SUCCESS;
        state.message = t('component.subscriptionDetails.customPackage.register.success');
        state.showMessage = true;
        state.actionSuccess = true;
      })
      .addCase(registerEmployer.rejected, (state, action) => {
        state.submitting = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(assignCustomPackage.pending, (state) => {
        state.submitting = true;
      })
      .addCase(assignCustomPackage.fulfilled, (state) => {
        state.submitting = false;
        state.status = SUCCESS;
        state.message = t('component.subscriptionDetails.customPackage.assign.success');
        state.showMessage = true;
        state.actionSuccess = true;
      })
      .addCase(assignCustomPackage.rejected, (state, action) => {
        state.submitting = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(assignPostpaidPackage.pending, (state) => {
        state.submitting = true;
      })
      .addCase(assignPostpaidPackage.fulfilled, (state) => {
        state.submitting = false;
        state.status = SUCCESS;
        state.message = t('component.subscriptionDetails.postpaidPackage.assign.success');
        state.showMessage = true;
        state.actionSuccess = true;
      })
      .addCase(assignPostpaidPackage.rejected, (state, action) => {
        state.submitting = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(cancelSubscription.pending, (state) => {
        state.submitting = true;
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.submitting = false;
        state.status = SUCCESS;
        state.message = t('component.subscriptionDetails.cancelSubscription.success');
        state.showMessage = true;
        state.actionSuccess = true;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.submitting = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(getCompanyList.pending, (state) => {
        state.companyOptionsLoading = true;
      })
      .addCase(getCompanyList.fulfilled, (state, action) => {
        state.companyOptionsLoading = false;
        state.companyOptions = action.payload;
      })
      .addCase(getCompanyList.rejected, (state) => {
        state.companyOptionsLoading = false;
        state.companyOptions = [];
      });
  },
});

export const { hideMessage } = employerSubscriptionSlice.actions;

export default employerSubscriptionSlice.reducer;
